import type { ChoiceAsDefault as NewDocumentChoiceAsDefault } from 'pages/NewDocument/types';
import type { AppropriateTextSaved } from 'pages/ECommerceCreate/types';
import type { ComparableDocument, IntDocumentRow } from 'pages/MyDocuments/types';
import type { WordCloudCircle } from 'functions/general/getCircles';
import type { Size } from 'pages/Ux/types';
import type { AnalyzedDocument } from 'pages/WebCheckerMultiple/types';
import type { WebCheckerField } from 'pages/WebCheckerECommerce/types';

type Limits = {
  max_chars: number;
  max_file_size: number;
  max_storage: number;
}

export type GA4Property = {
  name: string;
  display_name: string;
}

export type GA4Account = {
  selected: boolean;
  account_id: string;
  kind: string;
  name: string;
  username: string;
  properties: GA4Property[];
  id: number;
  user_id: number;
  property_name: string;
  property_display_name: string;
}

export type FacebookPage = {
  id: string;
  name: string;
  selected: boolean;
}

export type FacebookAccount = {
  id: string;
  name: string;
  instagram_business_account: {
    id: string,
    username: string
  }
}

export type MetaPaging = {
  cursors?: {
    before?: string;
    after?: string;
  }
  next?: string;
  previous?: string;
}

export type MetaPagingPost = {
  before?: string;
  after?: string;
}

export type FacebookComment = {
  id: string;
  message: string;
  like_count: number;
  created_time: string;
  username?: string;
  text?: string;
  from?: {
    name: string;
    id: string;
  }
  reactions: {
    data: {
      id: string;
      name: string;
      type: string;
    }[];
    summary: {
      total_count: number;
      viewer_reaction: string;
    }
  };
}

export type FacebookInsight = {
  name: string;
  period: string;
  values: { value: number}[];
  title: string;
  description: string;
  id: string;
}

export type FacebookPost = {
  id: string;
  created_time: string;
  message?: string;
  permalink_url: string;
  full_picture: string;
  shares: {count: number};
  reactions: {
    data: {
      id: string;
      name: string;
      type: string;
    }[];
    summary: {
      total_count: number;
      viewer_reaction: string;
    }
  };
  comments?: {
    data: FacebookComment[];
    paging: MetaPaging;
  }
  insights: {
    data: FacebookInsight[];
    paging: MetaPaging;
  }
}

export type InstagramComment = {
  id: string;
  like_count: number;
  timestamp: string;
  username: string;
  text: string;
  user?: {
    id: string;
  }
}

export type InstagramPost = {
  id: string;
  caption?: string;
  timestamp: string;
  like_count: number;
  comments_count: number;
  media_type: string;
  media_url?: string;
  comments?: {
    data: InstagramComment[];
  }
  insights?: {
    data: FacebookInsight[];
    paging: MetaPaging;
  }
}

export type MetaPostsResponse = {
  data: unknown[];
  paging: MetaPaging;
}

export type FacebookPostsResponse = MetaPostsResponse & {
  data: FacebookPost[];
}

export type InstagramPostsResponse = MetaPostsResponse & {
  data: InstagramPost[];
}

export type FacebookPostDetailsResponse = {
  full_picture?: string;
  attachments?: {
    data: {
      media_type: string;
      media: {
        image: {
          height: number;
          src: string;
          width: number;
        }
      }
    }[];
  };
  id: string;
}

export type InstagramPostDetailsResponse = {
  media_type: string;
  media_url?: string;
  id: string;
}

export type FacebookPageWithInstagramAccounts = {
  id: string;
  name: string;
  instagram_business_account: {
    id: string,
    username: string
  }
  selected: boolean;
}

export type FacebookAdInsights = {
  spend: string;
  clicks: string;
  reach: string;
  impressions: string;
  actions: { action_type: string; value: string }[];
  date_start: string;
  date_stop: string;
  ctr: string;
  cpp: string;
  cpm: string;
  cpc: string;
}

type FacebookAdLabel = {
  name: string;
  id: string;
}

type FacebookAssetFeedSpec = {
  images?: {
    adlabels: FacebookAdLabel[];
    hash: string;
    image_crops?: Record<string, [number, number][]>;
  }[];
  bodies: {
    adlabels?: FacebookAdLabel[];
    text: string;
  }[];
  call_to_action_types?: string[];
  call_to_actions?: {
    type: string;
    value: {
      app_destination: string;
      link?: string;
      app_link?: string;
    };
  }[];
  descriptions?: { text: string }[];
  link_urls?: {
    adlabels: FacebookAdLabel[];
    website_url: string;
    display_url: string;
  }[];
  titles?: {
    adlabels: FacebookAdLabel[];
    text: string;
  }[];
  ad_formats?: string[];
  optimization_type: string;
  additional_data?: {
    multi_share_end_card: boolean;
    page_welcome_message: string;
    is_click_to_message: boolean;
  };
  reasons_to_shop?: boolean;
  shops_bundle?: boolean;
}

export type FacebookAdCreative = {
  instagram_permalink_url: string;
  name: string;
  body?: string;
  video_id?: string;
  object_story_id?: string;
  call_to_action_type?: string;
  thumbnail_url: string;
  object_type: string;
  status: string;
  effective_instagram_media_id: string;
  effective_object_story_id: string;
  id: string;
  asset_feed_spec: FacebookAssetFeedSpec;
  object_story_spec?: {
    page_id: string;
    instagram_user_id: string;
  }
}

export type FacebookAd = {
  name: string;
  insights?: {
    data: FacebookAdInsights[];
    paging: MetaPaging;
  };
  adcreatives: {
    data: FacebookAdCreative[];
  }
  id: string;
}

export type FacebookAdsResponse = {
  data: FacebookAd[];
  paging: MetaPaging;
}

export type FacebookAdsAccountResponse = {
  data: {
    id: string;
    name: string;
    account_status: number;
  }[];
  paging: MetaPaging;
}

export const ACCOUNT_TYPES = {
  analytics: 'analytics',
  correlations: 'correlations',
  basic: 'basic'
} as const;
export const USER_TYPES = {
  personal: 'personal',
  business: 'business',
  licenced: 'licenced',
  trial: 'trial'
} as const;

export type ACCOUNT_TYPE = typeof ACCOUNT_TYPES[keyof typeof ACCOUNT_TYPES] | null;
export type USER_TYPE = typeof USER_TYPES[keyof typeof USER_TYPES] | null;

export type IntUser = {
  credits: number | null;
  email: string;
  first_name: string;
  is_active: boolean;
  is_superuser: boolean;
  last_name: string;
  limits: Limits;
  has_subscription: boolean;
  company: string | null;
  profession: string | null;
  company_location: string | null;
  native_language: string | null;
  english_level: string | null;
  prepaid: boolean;
  user_type: USER_TYPE;
  account_type: ACCOUNT_TYPE;
  ga4_accounts: GA4Account[];
}

export type IntUserResponse = {
  access_token: string;
  token_type: string;
  user: IntUser;
}

export type ChoiceAsDefault = {
  target: string;
  subject: string;
}

export type LocalStorageFunctions = {
  writeObject: <T>(name: string, data: T) => void;
  readObject: <T>(name: string) => T | undefined;
  removeObject(name: string): void
}

export type LocalAuth = {
  getUser: () => IntUserResponse | undefined;
  setUser: (user: IntUserResponse) => void;
  delUser: () => void;
  setUserUser: (user: IntUser) => void;
  isPremium: () => boolean;
  isLoggedIn: () => boolean;
  isAnalytics: () => boolean;
  hasSubscription: () => boolean;
  setHasSubscription: (value: boolean) => void;
  getMaxChars: () => number;
  getMaxPDFSize: () => number;
  isAdmin: () => boolean;
  isPrepaid: () => boolean;
  isTrial: () => boolean;
  isPersonal: () => boolean;
  isBusiness: () => boolean;
  hasLicence: () => boolean;
  canShare: () => boolean;
  hasOnlyLicence: () => boolean;
  hasMyDocuments: () => boolean;
  removeOneCredit: () => void;
  updateCredits: (credits: number) => void;
}

export type LocalCardStates = {
  getLocalStorageCardState: (id: string, defaultValue: boolean) => boolean;
  setLocalStorageCardState: (id: string, isOpen: boolean) => void;
}

export type LocalCookies = {
  getLocalStorageCookies: () => boolean;
  setLocalStorageCookies: () => void;
}

export type LocalFirstTime = {
  getLocalStorageFirstTime: () => boolean;
  setLocalStorageFirstTime: () => void;
}

export type LocalPreferences = {
  addOne: (key: string, value: boolean) => void;
  getOne: (key: string) => boolean | undefined;
  SUGGESTION_FLAG: string;
}

export type LocalWordCloud = {
  getLocalStorageWordCloud: (documentId: string) => WordCloudCircle[] | undefined;
  setLocalStorageWordCloud: (data: string, documentId: string) => void;
  delLocalStorageWordCloud: (documentId: string) => void;
}

export type LocalWebCheckerEcommerceDetails = {
  getLocalStorageWebCheckerEcommerceDetails: (url: string) => string| null;
  setLocalStorageWebCheckerEcommerceDetails: (url: string, doc: string) => void;
  delLocalStorageWebCheckerEcommerceDetails: (url: string) => void;
}

export type LocalWebCheckerDocuments = {
  getLocalStorageWebCheckerDocuments: (url: string) => AnalyzedDocument | null;
  setLocalStorageWebCheckerDocuments: (url: string, doc: AnalyzedDocument) => void;
  delLocalStorageWebCheckerDocuments: (url: string) => void;
}

export type LocalChoiceAsDefault = {
  getLocalStorageChoiceAsDefault: () => NewDocumentChoiceAsDefault | undefined;
  setLocalStorageChoiceAsDefault: (data: NewDocumentChoiceAsDefault) => void;
  delLocalStorageChoiceAsDefault: () => void;
}

export type LocalProductSaved = {
  getLocalStorageProductSaved: () => AppropriateTextSaved[];
  setLocalStorageProductSaved: (data: AppropriateTextSaved[]) => void;
  delLocalStorageProductSaved: () => void;
}

export type LocalECommerceFields = {
  getLocalStorageECommerceFields: (domain: string) => WebCheckerField[];
  setLocalStorageECommerceFields: (domain: string, data: WebCheckerField[]) => void;
  delLocalStorageECommerceFields: (domain: string) => void;
}

export type LocalSummaryOrder = {
  getLocalStorageSummaryOrder: () => string[];
  setLocalStorageSummaryOrder: (data: string[]) => void;
  delLocalStorageSummaryOrder: () => void;
}

export type LocalCompareDocument = {
  getCompareDocument: () => ComparableDocument | undefined;
  setCompareDocument: (data: ComparableDocument | undefined) => void;
  delCompareDocument: () => void;
}

export type LocalLeftMenuState = {
  getLocalStorageLeftMenuState: () => boolean;
  setLocalStorageLeftMenuState: (isClosed: boolean) => void;
}

export type LocalMobileWarning = {
  getLocalStorageMobileWarning: () => boolean;
  setLocalStorageMobileWarning: () => void;
}

export type LocalUxFeaturesOrder = {
  getLocalStorageUxFeaturesOrder: () => string[];
  setLocalStorageUxFeaturesOrder: (data: string[]) => void;
  delLocalStorageUxFeaturesOrder: () => void;
}

export type LocalGA4Email = {
  getLocalStorageGA4Email: () => string | undefined;
  setLocalStorageGA4Email: (data: string) => void;
}

export type IntegrationsWebSite = {
  website: string;
  state: string;
}

export type LocalIntegrationsWebsite = {
  getWebsite: () => IntegrationsWebSite | undefined;
  setWebsite: (data: IntegrationsWebSite) => void;
}

export type LocalInstagramEmail = {
  getLocalStorageInstagramEmail: () => string | undefined;
  setLocalStorageInstagramEmail: (data: string) => void;
}

export type LocalTiktokEmail = {
  getLocalStorageTiktokEmail: () => string | undefined;
  setLocalStorageTiktokEmail: (data: string) => void;
}

export type LocalSelectedDocuments = {
  get: () => IntDocumentRow[];
  set: (data: IntDocumentRow) => void;
  remove: () => void;
}

export type LocalWordCloudSearched = {
  get: (id: string, defaultValue: boolean) => boolean;
  set: (id: string, isOpen: boolean) => void;
}

export type LocalNotEditableWarning = {
  getLocalStorageNotEditableWarning: () => boolean;
  setLocalStorageNotEditableWarning: () => void;
}

export type LocalCustomerMetricsOrder = {
  getLocalStorageCustomerMetricsOrder: () => string[];
  setLocalStorageCustomerMetricsOrder: (data: string[]) => void;
  delLocalStorageCustomerMetricsOrder: () => void;
}

export type LocalTutorials = {
  getTutorials: () => string[];
  setTutorialIsDone: (id: string) => void;
  clearTutorials: () => void;
}

export type LocalTouchPointZoom = {
  setTouchPointZoom: (rootId: string, size: Size) => void;
  getTouchPointZoom: (rootId: string) => Size | null;
}

export type LocalAnalysis = {
  getLastFullReportId: () => string | undefined;
  setLastFullReportId: (id: string) => void;
  delLastFullReportId: () => void;
}

export type Local = {
  auth: LocalAuth;
  cookies: LocalCookies;
  analysis: LocalAnalysis;
  ga4Email: LocalGA4Email;
  instagramEmail: LocalInstagramEmail;
  tiktokEmail: LocalTiktokEmail;
  firstTime: LocalFirstTime;
  tutorials: LocalTutorials;
  wordCloud: LocalWordCloud;
  cardStates: LocalCardStates;
  preferences: LocalPreferences;
  productSaved: LocalProductSaved;
  summaryOrder: LocalSummaryOrder;
  leftMenuState: LocalLeftMenuState;
  mobileWarning: LocalMobileWarning;
  touchPointsZoom: LocalTouchPointZoom;
  choiceAsDefault: LocalChoiceAsDefault;
  compareDocument: LocalCompareDocument;
  uxFeaturesOrder: LocalUxFeaturesOrder;
  eCommerceFields: LocalECommerceFields;
  selectedDocuments: LocalSelectedDocuments;
  wordCloudSearched: LocalWordCloudSearched;
  notEditableWarning: LocalNotEditableWarning;
  webCheckerDocuments: LocalWebCheckerDocuments;
  integrationsWebSite: LocalIntegrationsWebsite;
  customerMetricsOrder: LocalCustomerMetricsOrder;
  webCheckerEcommerceDetails: LocalWebCheckerEcommerceDetails;
}
